import React, { useState, Suspense, useEffect } from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { Features } from './components/Features';
import { PremiumFeatures } from './components/PremiumFeatures';
import { AppDownloadSection } from './components/AppDownloadSection';
import { Pricing } from './components/Pricing';
import { Newsletter } from './components/Newsletter';
import { Footer } from './components/Footer';
import { BoltNewBadge } from './components/ui/BoltNewBadge';
import { AIDemo } from './components/AIDemo';
import { useTheme } from './hooks/useTheme';
import { blogPosts } from './components/BlogList';

// Lazy load page components for better performance
const About = React.lazy(() => import('./components/About').then(module => ({ default: module.About })));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const TermsOfService = React.lazy(() => import('./components/TermsOfService').then(module => ({ default: module.TermsOfService })));
const ContactUs = React.lazy(() => import('./components/ContactUs').then(module => ({ default: module.ContactUs })));
const ErrorPage = React.lazy(() => import('./components/ErrorPage').then(module => ({ default: module.ErrorPage })));
const FAQ = React.lazy(() => import('./components/FAQ').then(module => ({ default: module.FAQ })));
const Support = React.lazy(() => import('./components/Support').then(module => ({ default: module.Support })));
const BlogList = React.lazy(() => import('./components/BlogList').then(module => ({ default: module.BlogList })));
const BlogPost = React.lazy(() => import('./components/BlogPost').then(module => ({ default: module.BlogPost })));

type Page = 'home' | 'about' | 'privacy' | 'terms' | 'contact' | 'error' | 'faq' | 'support' | 'blog' | 'blogPost';

// SEO metadata for each page
const pageMetadata = {
  home: {
    title: 'Octify - AI-Powered Relationship Insights | Connect Better, Grow Together',
    description: 'Transform your relationships with AI-powered insights from Gemini 2.5 Flash. Get personalized advice, VR role-play scenarios, and wearable integration to build stronger connections with family, friends, colleagues, and pets.',
    keywords: 'relationship app, AI relationship advice, relationship insights, communication skills, family relationships, workplace relationships, pet bonding, VR role-play, emotional intelligence, relationship psychology, Gemini AI, wearable integration, relationship coaching, personal growth'
  },
  about: {
    title: 'About Octify - Our Mission to Transform Relationships with AI Technology',
    description: 'Learn about Octify\'s mission to help people build stronger relationships through AI-powered insights, privacy-first design, and science-backed recommendations. Discover our story, values, and commitment to your privacy.',
    keywords: 'about Octify, relationship technology, AI relationship platform, privacy-first design, relationship psychology, science-backed insights, company mission, relationship improvement, AI ethics, data privacy'
  },
  privacy: {
    title: 'Privacy Policy - How Octify Protects Your Relationship Data',
    description: 'Discover how Octify protects your relationship data with end-to-end encryption, privacy-first design, and complete user control over personal information. Learn about our data collection, usage, and your rights.',
    keywords: 'privacy policy, data protection, end-to-end encryption, relationship data privacy, GDPR compliance, user rights, data security, privacy-first design, personal information protection'
  },
  terms: {
    title: 'Terms of Service - Octify Usage Guidelines and Policies',
    description: 'Read Octify\'s terms of service including acceptable use, subscription terms, AI content disclaimers, and liability limitations. Understand your rights and responsibilities when using our platform.',
    keywords: 'terms of service, usage guidelines, subscription terms, AI disclaimers, acceptable use policy, liability limitations, user agreement, platform policies'
  },
  contact: {
    title: 'Contact Octify Support - Get Help with Your Relationship Journey',
    description: 'Get in touch with Octify\'s support team for help with relationship insights, technical issues, or general inquiries. We\'re here to help you succeed in your relationship growth journey.',
    keywords: 'contact support, customer service, relationship help, technical support, user assistance, help center, support team, customer care'
  },
  faq: {
    title: 'Frequently Asked Questions - Octify Help Center and Support',
    description: 'Find answers to common questions about Octify\'s AI features, privacy, billing, and technical requirements. Get the help you need quickly with our comprehensive FAQ section.',
    keywords: 'FAQ, frequently asked questions, help center, AI features, billing questions, technical support, user guide, troubleshooting, platform help'
  },
  support: {
    title: 'Octify Support Center - Comprehensive Help and Resources',
    description: 'Access comprehensive support resources including FAQ, contact options, community forums, and knowledge base articles for Octify users. Get the help you need to maximize your relationship growth.',
    keywords: 'support center, help resources, user guide, knowledge base, community forum, customer support, technical help, relationship guidance'
  },
  blog: {
    title: 'Octify Blog - Expert Relationship Insights and AI Tips',
    description: 'Read expert advice, AI insights, and real stories about relationship improvement. Discover tips for better communication, empathy, and connection from relationship psychology experts.',
    keywords: 'relationship blog, relationship advice, AI insights, communication tips, relationship psychology, expert advice, relationship improvement, emotional intelligence, relationship tips'
  },
  blogPost: {
    title: 'The Science Behind AI-Powered Relationship Insights - Octify Blog',
    description: 'Discover how Gemini 2.5 Flash analyzes communication patterns to provide personalized relationship guidance that actually works.',
    keywords: 'AI relationship insights, Gemini AI, relationship analysis, communication patterns, relationship science, AI technology, personalized advice'
  },
  error: {
    title: 'Error - Octify | Something Went Wrong',
    description: 'Something went wrong. Our team is working to fix the issue. Please try again or contact support if the problem persists.',
    keywords: 'error page, technical issue, website error, support help'
  }
};

// Loading component for Suspense fallback
const PageLoader: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-orange-100 via-blue-50 to-purple-100 dark:from-orange-900/20 dark:via-blue-900/20 dark:to-purple-900/20 flex items-center justify-center pt-20">
    <div className="text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full mb-4 animate-pulse">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
      <p className="text-lg text-gray-600 dark:text-gray-300">Loading...</p>
    </div>
  </div>
);

function App() {
  const { theme } = useTheme();
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [selectedBlogPostId, setSelectedBlogPostId] = useState<string | null>(null);
  const [showAIDemo, setShowAIDemo] = useState(false);

  // Debug theme changes
  useEffect(() => {
    console.log('Current theme:', theme);
    console.log('HTML element classes:', document.documentElement.className);
  }, [theme]);

  // Update document title and meta tags when page changes
  useEffect(() => {
    let metadata = pageMetadata[currentPage];
    
    // For blog posts, get specific metadata from the blog post data
    if (currentPage === 'blogPost' && selectedBlogPostId) {
      const blogPost = blogPosts.find(post => post.id === selectedBlogPostId);
      if (blogPost) {
        metadata = {
          title: `${blogPost.title} - Octify Blog`,
          description: blogPost.excerpt,
          keywords: blogPost.keywords || 'relationship insights, AI advice, relationship tips'
        };
      }
    }
    
    if (metadata) {
      // Update document title
      document.title = metadata.title;
      
      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', metadata.description);
      }
      
      // Update meta keywords
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', metadata.keywords);
      } else {
        // Create keywords meta tag if it doesn't exist
        const keywordsMeta = document.createElement('meta');
        keywordsMeta.name = 'keywords';
        keywordsMeta.content = metadata.keywords;
        document.head.appendChild(keywordsMeta);
      }
      
      // Update Open Graph title and description
      const ogTitle = document.querySelector('meta[property="og:title"]');
      const ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogTitle) ogTitle.setAttribute('content', metadata.title);
      if (ogDescription) ogDescription.setAttribute('content', metadata.description);
      
      // Update Twitter title and description
      const twitterTitle = document.querySelector('meta[property="twitter:title"]');
      const twitterDescription = document.querySelector('meta[property="twitter:description"]');
      if (twitterTitle) twitterTitle.setAttribute('content', metadata.title);
      if (twitterDescription) twitterDescription.setAttribute('content', metadata.description);
      
      // Update canonical URL
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        const baseUrl = 'https://octify.app';
        let pageUrl = baseUrl;
        
        if (currentPage === 'blogPost' && selectedBlogPostId) {
          const blogPost = blogPosts.find(post => post.id === selectedBlogPostId);
          if (blogPost) {
            // Create SEO-friendly URL slug from title
            const slug = blogPost.title.toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-+|-+$/g, '');
            pageUrl = `${baseUrl}/blog/${slug}`;
          }
        } else if (currentPage !== 'home') {
          pageUrl = `${baseUrl}/${currentPage}`;
        }
        
        canonical.setAttribute('href', pageUrl);
      }
    }
  }, [currentPage, selectedBlogPostId]);

  const navigateToPage = (page: Page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  const handlePrivacyAccept = () => {
    console.log('Privacy policy accepted');
    setCurrentPage('home');
  };

  const handleTermsAccept = () => {
    console.log('Terms accepted');
    setCurrentPage('home');
  };

  const handleContactSubmit = (data: any) => {
    console.log('Contact form submitted:', data);
  };

  const handleErrorRetry = () => {
    console.log('Retry action');
    setCurrentPage('home');
  };

  const handleContactSupport = () => {
    setCurrentPage('contact');
  };

  const handleGoHome = () => {
    setCurrentPage('home');
  };

  const handleCommunityForum = () => {
    console.log('Navigate to community forum');
  };

  const handleReadBlogPost = (postId: string) => {
    setSelectedBlogPostId(postId);
    setCurrentPage('blogPost');
  };

  const handleBackToBlog = () => {
    setSelectedBlogPostId(null);
    setCurrentPage('blog');
  };

  const handlePremiumUpgrade = () => {
    setShowAIDemo(true);
  };

  // Render different pages based on current page
  const renderPage = () => {
    switch (currentPage) {
      case 'about':
        return (
          <Suspense fallback={<PageLoader />}>
            <About />
          </Suspense>
        );
      case 'privacy':
        return (
          <Suspense fallback={<PageLoader />}>
            <PrivacyPolicy onAccept={handlePrivacyAccept} />
          </Suspense>
        );
      case 'terms':
        return (
          <Suspense fallback={<PageLoader />}>
            <TermsOfService onAccept={handleTermsAccept} />
          </Suspense>
        );
      case 'contact':
        return (
          <Suspense fallback={<PageLoader />}>
            <ContactUs 
              onSubmit={handleContactSubmit}
              onCommunityForum={handleCommunityForum}
            />
          </Suspense>
        );
      case 'error':
        return (
          <Suspense fallback={<PageLoader />}>
            <ErrorPage 
              errorType="general"
              onRetry={handleErrorRetry}
              onContactSupport={handleContactSupport}
              onGoHome={handleGoHome}
            />
          </Suspense>
        );
      case 'faq':
        return (
          <Suspense fallback={<PageLoader />}>
            <FAQ 
              onContactSupport={handleContactSupport}
            />
          </Suspense>
        );
      case 'support':
        return (
          <Suspense fallback={<PageLoader />}>
            <Support 
              onNavigateToFAQ={() => navigateToPage('faq')}
              onNavigateToContact={() => navigateToPage('contact')}
              onNavigateToBlog={() => navigateToPage('blog')}
            />
          </Suspense>
        );
      case 'blog':
        return (
          <Suspense fallback={<PageLoader />}>
            <BlogList 
              onReadPost={handleReadBlogPost}
            />
          </Suspense>
        );
      case 'blogPost':
        return (
          <Suspense fallback={<PageLoader />}>
            <BlogPost 
              postId={selectedBlogPostId || undefined}
              onBack={handleBackToBlog}
            />
          </Suspense>
        );
      default:
        return (
          <>
            <main>
              <Hero />
              <Features />
              <PremiumFeatures onUpgrade={handlePremiumUpgrade} />
              <AppDownloadSection />
              <Pricing />
              <Newsletter />
            </main>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <Header onNavigate={navigateToPage} currentPage={currentPage} />
      {renderPage()}
      <Footer onNavigate={navigateToPage} />
      
      {/* AI Demo Modal */}
      <AIDemo 
        isActive={showAIDemo} 
        onClose={() => setShowAIDemo(false)} 
      />
      
      {/* Bolt.new Badge - Floating on all pages */}
      <BoltNewBadge 
        position="bottom-left" 
        variant="auto" 
        size="medium"
      />
    </div>
  );
}

export default App;